<template>
  <div class="panel-section">
    <div class="section-title-row">
      <h3 class="section-title">GESTIÓN DE USUARIOS <span class="section-meta">{{ usuarios.length }} usuarios</span></h3>
      <button @click="mostrarFormNuevo = true" class="bb-btn-primary">+ NUEVO USUARIO</button>
    </div>

    <div class="table-wrapper">
      <table class="bb-table">
        <thead>
          <tr>
            <th>Usuario</th>
            <th>Nombre</th>
            <th>Rol</th>
            <th class="th-action">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in usuarios" :key="user.id">
            <td class="td-code">{{ user.username }}</td>
            <td class="td-desc">{{ user.nombre_completo || '—' }}</td>
            <td>
              <span class="rol-badge" :class="`rol-${user.rol}`">{{ user.rol.toUpperCase() }}</span>
            </td>
            <td class="td-action-btn">
              <button @click="editarUsuario(user)" class="bb-btn-inline bb-btn-edit">EDITAR</button>
              <button @click="eliminarUsuario(user)" :disabled="user.username === usuarioActual" class="bb-btn-inline bb-btn-delete-sm">ELIMINAR</button>
            </td>
          </tr>
          <tr v-if="usuarios.length === 0">
            <td colspan="4" class="empty-row">Sin usuarios registrados</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal formulario -->
    <div v-if="showModal" class="bb-modal-overlay" @click.self="cerrarModal">
      <div class="bb-modal-form">
        <div class="bb-modal-header">
          <h3>{{ usuarioEditando ? "EDITAR" : "NUEVO" }} USUARIO</h3>
          <button @click="cerrarModal" class="bb-close-btn"><PhX :size="13" weight="bold" /></button>
        </div>
        <div class="bb-modal-body">
          <div class="form-field">
            <label class="field-label">Username</label>
            <input v-model="form.username" :disabled="!!usuarioEditando" class="bb-input" />
          </div>
          <div class="form-field">
            <label class="field-label">Nombre completo</label>
            <input v-model="form.nombre_completo" class="bb-input" />
          </div>
          <div class="form-field">
            <label class="field-label">Rol</label>
            <select v-model="form.rol" class="bb-input">
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="viewer">Viewer</option>
              <option v-if="props.usuarioRol === 'superadmin'" value="superadmin">Superadmin</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">Categoría asignada</label>
            <input v-model="form.categoria_asignada" class="bb-input" />
          </div>
          <div class="form-field">
            <label class="field-label">Password</label>
            <input v-model="form.password" type="password" class="bb-input" :placeholder="usuarioEditando ? '(dejar vacío para no cambiar)' : ''" />
          </div>
          <p v-if="errorMsg" class="error-msg"><PhWarning :size="12" weight="bold" /> {{ errorMsg }}</p>
          <div class="form-actions">
            <button @click="cerrarModal" class="bb-btn-inline">CANCELAR</button>
            <button @click="guardarUsuario" class="bb-btn-primary">GUARDAR</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import { PhX, PhWarning } from '@phosphor-icons/vue'

const props = defineProps({ usuarioActual: String, usuarioRol: String });
const API = "https://dash-visaovip.kompara.tech/api";
const getH = () => ({ headers: { Authorization: "Bearer " + localStorage.getItem("token_precios") } });
const usuarios = ref([]);
const mostrarFormNuevo = ref(false);
const usuarioEditando = ref(null);
const errorMsg = ref("");
const form = ref({ username: "", nombre_completo: "", rol: "manager", categoria_asignada: "", password: "" });
const showModal = computed(() => mostrarFormNuevo.value || !!usuarioEditando.value);

const cargarUsuarios = async () => {
  const res = await axios.get(API + "/usuarios/", getH());
  usuarios.value = res.data;
};
const editarUsuario = (user) => {
  usuarioEditando.value = user;
  form.value = { username: user.username, nombre_completo: user.nombre_completo || "", rol: user.rol, categoria_asignada: user.categoria_asignada || "", password: "" };
};
const cerrarModal = () => {
  mostrarFormNuevo.value = false;
  usuarioEditando.value = null;
  errorMsg.value = "";
  form.value = { username: "", nombre_completo: "", rol: "manager", categoria_asignada: "", password: "" };
};
const guardarUsuario = async () => {
  errorMsg.value = "";
  try {
    if (usuarioEditando.value) {
      const d = { ...form.value };
      if (!d.password) delete d.password;
      await axios.put(API + "/usuarios/" + usuarioEditando.value.id, d, getH());
    } else {
      if (!form.value.password) { errorMsg.value = "Password requerido"; return; }
      await axios.post(API + "/usuarios/", form.value, getH());
    }
    await cargarUsuarios();
    cerrarModal();
  } catch (e) {
    errorMsg.value = (e.response && e.response.data && e.response.data.detail) || "Error";
  }
};
const eliminarUsuario = async (user) => {
  if (!confirm("Eliminar " + user.username)) return;
  await axios.delete(API + "/usuarios/" + user.id, getH());
  await cargarUsuarios();
};
onMounted(cargarUsuarios);
</script>

<style scoped>
.panel-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  overflow: hidden;
}
.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
}
.section-title {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.section-meta {
  font-family: var(--font-mono);
  font-size: 9.5px;
  color: var(--color-text-faint);
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: none;
}

/* ========== TABLA ========== */
.table-wrapper {
  overflow-x: auto;
}
.bb-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-mono);
}
.bb-table thead th {
  text-align: left;
  padding: 7px 12px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  border-bottom: 1px solid var(--color-border-strong);
  background: var(--color-surface-2);
}
.bb-table thead .th-action { text-align: right; }
.bb-table tbody td {
  padding: 8px 12px;
  font-size: 11.5px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
}
.bb-table tbody tr:hover { background: var(--color-hover-row); }
.bb-table tbody tr:last-child td { border-bottom: none; }

.td-code {
  font-family: var(--font-mono);
  color: var(--color-text);
  font-weight: 600;
}
.td-desc {
  font-family: 'Inter', sans-serif;
  color: var(--color-text);
}
.td-action-btn {
  text-align: right;
  white-space: nowrap;
}
.td-action-btn button {
  margin-left: 6px;
}
.empty-row {
  padding: 20px !important;
  text-align: center;
  color: var(--color-text-faint);
  font-style: italic;
}

/* ========== ROL BADGES ========== */
.rol-badge {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 2px;
  letter-spacing: 0.08em;
  border: 1px solid;
}
.rol-admin {
  background: var(--color-leader-bg);
  color: var(--color-leader-text);
  border-color: var(--color-leader);
}
.rol-manager {
  background: rgba(56, 189, 248, 0.10);
  color: var(--color-min-price-text);
  border-color: rgba(56, 189, 248, 0.35);
}
.rol-viewer {
  background: var(--color-success-bg);
  color: var(--color-success-text);
  border-color: var(--color-success);
}
.rol-superadmin {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border-color: var(--color-accent-border);
}

/* ========== BOTONES ========== */
.bb-btn-primary {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border: 1px solid var(--color-accent-border);
  padding: 5px 12px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  transition: all 0.15s;
}
.bb-btn-primary:hover {
  background: var(--color-accent);
  color: var(--color-surface-3);
}
.bb-btn-inline {
  background: transparent;
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  padding: 4px 10px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.15s;
}
.bb-btn-inline:hover {
  color: var(--color-text);
  border-color: var(--color-text-muted);
}
.bb-btn-edit:hover {
  color: var(--color-accent);
  border-color: var(--color-accent-border);
}
.bb-btn-delete-sm {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
.bb-btn-delete-sm:hover:not(:disabled) {
  background: var(--color-danger-bg);
}
.bb-btn-delete-sm:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* ========== MODAL ========== */
.bb-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.bb-modal-form {
  background: var(--color-surface);
  border: 1px solid var(--color-accent-border);
  border-radius: 3px;
  width: 440px;
  max-width: 92%;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
.bb-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
}
.bb-modal-header h3 {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
}
.bb-close-btn {
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
  padding: 3px 8px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 11px;
  font-family: var(--font-mono);
  transition: all 0.15s;
}
.bb-close-btn:hover {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
.bb-modal-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label {
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: var(--color-text-muted);
  text-transform: uppercase;
}
.bb-input {
  padding: 7px 10px;
  border-radius: 2px;
  border: 1px solid var(--color-border-strong);
  background: var(--color-background);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 11.5px;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.bb-input:focus {
  outline: none;
  border-color: var(--color-accent);
}
.bb-input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.bb-input::placeholder {
  color: var(--color-text-faint);
  font-style: italic;
}
.error-msg {
  margin: 0;
  padding: 6px 10px;
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger);
  color: var(--color-danger-text);
  font-family: var(--font-mono);
  font-size: 11px;
  border-radius: 2px;
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 4px;
}
</style>